// Phase 2.7 — Right-side settlement drawer extracted from
// MonthlyAccounting.jsx. Shows the full build-up of one (month, partner)
// payable + payment lines + statement download. The orchestrator owns
// the panel state and the email/PDF handlers.

import { api, apiUrl, fmtINR, fmtPeriod } from "@/lib/api";
import { Btn } from "@/components/Shared";
import { CheckCircle2, Circle, Plus, Pencil, Trash2, X, FileText, Mail } from "lucide-react";
import { toast } from "sonner";

export default function SettlementPanel({
  settlement,
  onClose,
  onOpenAlloc,
  onAddPayment,
  onEditPayment,
  onDeletePayment,
}) {
  if (!settlement) return null;
  const s = settlement.summary;
  const pers = settlement.person;
  const lo = pers.toLowerCase();
  const lines = s[`${lo}_payment_lines`] || [];
  const sal = Number(s[`${lo}_salary_alloc`] || 0);
  const reimb = Number(s[`${lo}_reimbursement`] || 0);
  const profit = Number(s.profit_share || 0);
  const grossPayable = Number(s[`${lo}_payable_gross`] || 0);
  const settled = Number(s[`${lo}_settled`] || 0);  // eslint-disable-line no-unused-vars
  const outstanding = Number(s[`${lo}_outstanding`] || 0);
  const status = s[`${lo}_status`];

  const onEmailStatement = async () => {
    try {
      const res = await api.post("/email/settlement", { person: pers, until_month: settlement.month });
      toast.success(`Statement emailed to ${res.data.to}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (e?.message || "Failed");
      toast.error(msg);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-30" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 w-[680px] max-w-full bg-[var(--surface)] z-40 shadow-2xl overflow-y-auto" data-testid="settlement-panel">
        <div className="p-6 border-b border-[var(--line)] flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Settlement</div>
            <div className="text-xl font-semibold mt-1">{pers} · {fmtPeriod(settlement.month)}</div>
            <div className="mt-2">
              {status === "settled" && <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm text-[11px] font-semibold uppercase tracking-wider"><CheckCircle2 size={13} /> Settled</span>}
              {status === "outstanding" && <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-sm text-[11px] font-semibold uppercase tracking-wider"><Circle size={13} /> ₹{fmtINR(outstanding, 0)} due</span>}
              {status === "overpaid" && <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-sm text-[11px] font-semibold uppercase tracking-wider"><CheckCircle2 size={13} /> Overpaid ₹{fmtINR(Math.abs(outstanding), 0)}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm" data-testid="settlement-close"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold mb-2">Build-up</div>
            <div className="overflow-x-auto"><table className="table-data w-full">
              <tbody>
                <tr><td className="text-[var(--muted)]">+ Profit share (50% of net revenue)</td><td className="text-right num">{fmtINR(profit, 0)}</td></tr>
                <tr><td className="text-[var(--muted)]">+ Reimbursement (personal payments)</td><td className="text-right num">{fmtINR(reimb, 0)}</td></tr>
                <tr className="border-t border-[var(--line)] font-semibold"><td>Gross payable</td><td className="text-right num">{fmtINR(grossPayable, 0)}</td></tr>
                <tr><td className="text-[var(--muted)]">− Salary alloc (auto-deducted) <button className="text-[10px] underline ml-1" onClick={() => onOpenAlloc(s, pers)}>view</button></td><td className="text-right num text-[var(--negative)]">−{fmtINR(sal, 0)}</td></tr>
                <tr><td className="text-[var(--muted)]">− Recorded payments ({lines.length} line{lines.length === 1 ? "" : "s"})</td><td className="text-right num text-[var(--negative)]">−{fmtINR(s[`${lo}_payment_total`] || 0, 0)}</td></tr>
                <tr className="border-t-2 border-[var(--ink)] font-semibold"><td>{outstanding > 1 ? "Outstanding" : outstanding < -1 ? "Overpaid" : "Settled"}</td><td className={`text-right num ${outstanding > 1 ? "text-[var(--accent)]" : "text-[var(--positive)]"}`}>{fmtINR(Math.abs(outstanding), 0)}</td></tr>
              </tbody>
            </table></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Payment Lines</div>
              <Btn variant="accent" testid="settle-add-pay" onClick={() => onAddPayment(settlement.month, pers, Math.max(outstanding, 0))}>
                <Plus size={12} /> Add payment
              </Btn>
            </div>
            <div className="overflow-x-auto"><table className="table-data w-full">
              <thead><tr><th>Date</th><th>Method</th><th className="text-right">Amount</th><th>Notes</th><th></th></tr></thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id} data-testid={`settle-line-${l.id}`}>
                    <td className="num text-[12px]">{l.paid_date || "—"}</td>
                    <td><span className="inline-flex items-center px-1.5 py-0.5 bg-[var(--surface-2)] border border-[var(--line-2)] rounded-sm text-[11px]">{l.paid_via}</span></td>
                    <td className="text-right num text-[var(--positive)]">{fmtINR(l.amount_paid, 0)}</td>
                    <td className="text-[11px] text-[var(--muted)] max-w-xs truncate">{l.notes || "—"}</td>
                    <td className="text-right">
                      <button onClick={() => onEditPayment(l)} className="p-1 hover:bg-[var(--surface-2)] rounded-sm"><Pencil size={12} /></button>
                      <button onClick={() => onDeletePayment(l.id)} className="p-1 hover:bg-red-50 text-[var(--negative)] rounded-sm"><Trash2 size={12} /></button>
                    </td>
                  </tr>
                ))}
                {!lines.length && <tr><td colSpan={5} className="text-center text-[var(--muted)] py-3 text-[12px]">No payments recorded yet for this month. Click "Add payment" if {pers} has been paid via voucher / bank / etc.</td></tr>}
              </tbody>
            </table></div>
          </div>

          <div className="border-t border-[var(--line)] pt-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold mb-2">Settlement Statement</div>
            <div className="text-[11px] text-[var(--muted)] mb-2">Generates a printable PDF with running balances from the earliest month through {fmtPeriod(settlement.month)}.</div>
            <div className="flex flex-wrap gap-2">
              <Btn variant="ghost" testid="settle-pdf" onClick={() => window.open(apiUrl(`/monthly-accounting/settlement.pdf?person=${pers}&until_month=${settlement.month}`), "_blank")}>
                <FileText size={14} /> Download {pers} Statement (PDF)
              </Btn>
              <Btn variant="accent" testid="settle-email" onClick={onEmailStatement}>
                <Mail size={14} /> Email to {pers}
              </Btn>
            </div>
            <div className="text-[10.5px] text-[var(--muted)] mt-2">Recipient + sender configured in <a className="underline text-[var(--accent)]" href="/settings">Settings</a>.</div>
          </div>
        </div>
      </aside>
    </>
  );
}
